const CONSENT_KEY = "dad_consent_v1";
const CONSENT_VERSION = 2;
const CONSENT_MAX_AGE = 365 * 24 * 60 * 60 * 1000; // re-ask after 12 months (ICO guidance)
const UTM_KEY = "dad_attribution";
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];
const ID_PATTERNS = { ga4_id: /^G-[A-Z0-9]{4,16}$/, gtm_id: /^GTM-[A-Z0-9]{4,12}$/, meta_pixel_id: /^\d{8,20}$/ };

export const validId = (key, v) => !!v && ID_PATTERNS[key].test(String(v).trim());

export const getConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(CONSENT_KEY));
    if (!c || c.v !== CONSENT_VERSION || Date.now() - (c.ts || 0) > CONSENT_MAX_AGE) return null;
    return c;
  } catch { return null; }
};

const TRACKING_COOKIES = /^(_ga|_gid|_gat|_gcl_|_fbp|_fbc)/;
const clearTrackingCookies = () => {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter((n) => TRACKING_COOKIES.test(n)).forEach((n) => {
    domains.forEach((d) => { document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`; });
  });
};

const consentState = (c) => ({
  analytics_storage: c?.analytics ? "granted" : "denied",
  ad_storage: c?.marketing ? "granted" : "denied",
  ad_user_data: c?.marketing ? "granted" : "denied",
  ad_personalization: c?.marketing ? "granted" : "denied",
});

// Saves the choice. If consent was withdrawn for anything already running, cookies are
// cleared and the page reloads so no tracking script keeps running.
export const saveConsent = (c) => {
  const prev = getConsent();
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ analytics: !!c.analytics, marketing: !!c.marketing, v: CONSENT_VERSION, ts: Date.now() }));
  if (window.gtag) window.gtag("consent", "update", consentState(c));
  const revoked = (prev?.analytics && !c.analytics) || (prev?.marketing && !c.marketing);
  if (revoked) {
    if (window.fbq && !c.marketing) window.fbq("consent", "revoke");
    clearTrackingCookies();
    sessionStorage.removeItem(UTM_KEY);
    window.location.reload();
    return;
  }
  window.dispatchEvent(new Event("dad-consent"));
};

const urlUtm = () => {
  const p = new URLSearchParams(window.location.search);
  return UTM_FIELDS.reduce((a, k) => (p.get(k) ? { ...a, [k]: p.get(k) } : a), {});
};

// Persist attribution across pages only with analytics consent; otherwise read the current URL only.
export function captureAttribution() {
  const u = urlUtm();
  if (getConsent()?.analytics && Object.keys(u).length) sessionStorage.setItem(UTM_KEY, JSON.stringify(u));
}
export function getAttribution() {
  let stored = {};
  if (getConsent()?.analytics) {
    try { stored = JSON.parse(sessionStorage.getItem(UTM_KEY)) || {}; } catch { stored = {}; }
  }
  return { ...stored, ...urlUtm() };
}

const addScript = (id, src, inline) => {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  if (src) { s.async = true; s.src = src; } else { s.text = inline; }
  document.head.appendChild(s);
};

// Google Consent Mode v2: defaults are denied, then updated to the visitor's choice.
const initGtag = (c) => {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("consent", "default", { ...consentState(null), wait_for_update: 500 });
  window.gtag("set", "ads_data_redaction", true);
  window.gtag("consent", "update", consentState(c));
};

export function loadAnalytics(settings) {
  const c = getConsent();
  if (!c) return;
  const s = settings || {};
  const gtm = validId("gtm_id", s.gtm_id) && s.gtm_id.trim();
  const ga4 = validId("ga4_id", s.ga4_id) && s.ga4_id.trim();
  const pixel = validId("meta_pixel_id", s.meta_pixel_id) && s.meta_pixel_id.trim();
  if (c.analytics && (gtm || ga4)) initGtag(c);
  if (c.analytics && gtm) {
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    addScript("dad-gtm", `https://www.googletagmanager.com/gtm.js?id=${gtm}`);
  }
  if (c.analytics && ga4) {
    addScript("dad-ga4-lib", `https://www.googletagmanager.com/gtag/js?id=${ga4}`);
    if (!window.__dadGa4) { window.__dadGa4 = true; window.gtag("js", new Date()); window.gtag("config", ga4, { anonymize_ip: true }); }
  }
  if (c.marketing && pixel) {
    addScript("dad-pixel", null, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('consent','grant');fbq('init','${pixel}');fbq('track','PageView');`);
  }
}

export function trackLead(formType) {
  const c = getConsent();
  if (c?.analytics && window.gtag) window.gtag("event", "generate_lead", { form_type: formType });
  if (c?.analytics && window.dataLayer) window.dataLayer.push({ event: "enquiry_submitted", form_type: formType });
  if (c?.marketing && window.fbq) window.fbq("track", "Lead");
}
